import { useState } from 'react';
import { Connection } from '../../services/connectionService';
import { sharepointService } from '../../services/sharepointService';
import { useConfirm } from '../../hooks/useConfirm';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  TrashIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { SharePointSetupModal } from './SharePointSetupModal';

interface SharePointConnectionProps {
  connection?: Connection;
  onUpdate: () => void;
}

export function SharePointConnection({ connection, onUpdate }: SharePointConnectionProps) {
  const [showSetupModal, setShowSetupModal] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();
  
  const handleConnect = async () => {
    try {
      await sharepointService.startOAuthFlow();
    } catch (error) {
      console.error('Failed to start SharePoint OAuth:', error);
      toast.error('Failed to connect to SharePoint');
    }
  };
  
  const handleDisconnect = async () => {
    if (!connection) return;
    
    const confirmed = await confirm({
      title: 'Disconnect SharePoint?',
      message: 'This will remove the connection. Document links will no longer work until you reconnect.',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    
    if (!confirmed) return;
    
    const toastId = toast.loading('Disconnecting...');
    
    try {
      await sharepointService.deleteConnection(connection.id);
      toast.success('SharePoint disconnected', { id: toastId });
      onUpdate();
    } catch (error) {
      console.error('Failed to disconnect:', error);
      toast.error('Failed to disconnect', { id: toastId });
    }
  };
  
  const handleOpenSetup = () => {
    setShowSetupModal(true);
  };

  // Parse connection config for display
  const siteUrl = connection?.config?.site_url as string | undefined;
  const siteName = connection?.config?.site_name as string | undefined;
  const foldersSetup = connection?.config?.folders as Record<string, string> | undefined;

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
            <svg
              className="w-7 h-7 text-white"
              role="img"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-label="Microsoft SharePoint"
            >
              <path d="M24 13.5q0 1.242-.475 2.332-.474 1.09-1.289 1.904-.814.815-1.904 1.29-1.09.474-2.332.474-.762 0-1.523-.2-.106.997-.557 1.858-.451.862-1.154 1.494-.704.633-1.606.99-.902.358-1.91.358-1.09 0-2.045-.416-.955-.416-1.664-1.125-.709-.709-1.125-1.664Q6 19.84 6 18.75q0-.188.018-.375.017-.188.04-.375H.997q-.41 0-.703-.293T0 17.004V6.996q0-.41.293-.703T.996 6h3.54q.14-1.277.726-2.373.586-1.096 1.488-1.904Q7.652.914 8.807.457 9.96 0 11.25 0q1.395 0 2.625.533T16.02 1.98q.914.915 1.447 2.145T18 6.75q0 .188-.012.375-.011.188-.035.375 1.242 0 2.344.469 1.101.468 1.928 1.277.826.809 1.3 1.904Q24 12.246 24 13.5zm-12.75-12q-.973 0-1.857.34-.885.34-1.577.943-.691.604-1.154 1.43Q6.2 5.039 6.06 6h4.945q.41 0 .703.293t.293.703v4.945l.21-.035q.212-.75.61-1.424.399-.673.944-1.218.545-.545 1.213-.944.668-.398 1.43-.61.093-.503.093-.96 0-1.09-.416-2.045-.416-.955-1.125-1.664-.709-.709-1.664-1.125Q12.34 1.5 11.25 1.5zM6.117 15.902q.54 0 1.06-.111.522-.111.932-.37.41-.257.662-.679.252-.422.252-1.055 0-.632-.263-1.054-.264-.422-.662-.703-.399-.282-.856-.463l-.855-.34q-.399-.158-.662-.334-.264-.176-.264-.445 0-.2.14-.323.141-.123.335-.193.193-.07.404-.094.21-.023.351-.023.598 0 1.055.152.457.153.95.457V8.543q-.282-.082-.522-.14-.24-.06-.475-.1-.234-.041-.486-.059-.252-.017-.557-.017-.515 0-1.054.117-.54.117-.979.375-.44.258-.715.68-.275.421-.275 1.03 0 .598.263.997.264.398.663.68.398.28.855.474l.856.363q.398.17.662.358.263.187.263.457 0 .222-.123.351-.123.13-.31.2-.188.07-.393.087-.205.018-.369.018-.703 0-1.248-.234-.545-.235-1.107-.621v1.875q1.195.468 2.472.468zM11.25 22.5q.773 0 1.453-.293t1.19-.803q.51-.51.808-1.195.299-.686.299-1.459 0-.668-.223-1.277-.222-.61-.62-1.096-.4-.486-.95-.826-.55-.34-1.207-.48v1.933q0 .41-.293.703t-.703.293H7.57q-.07.375-.07.75 0 .773.293 1.459t.803 1.195q.51.51 1.195.803.686.293 1.459.293zM18 18q.926 0 1.746-.352.82-.351 1.436-.966.615-.616.966-1.43.352-.815.352-1.752 0-.926-.352-1.746-.351-.82-.966-1.436-.616-.615-1.436-.966Q18.926 9 18 9t-1.74.357q-.815.358-1.43.973t-.973 1.43q-.357.814-.357 1.74 0 .129.006.258t.017.258q.551.27 1.02.65t.838.855q.369.475.627 1.026.258.55.387 1.148Q17.18 18 18 18Z"/>
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">SharePoint</h3>
            <p className="text-sm text-gray-500">Document storage and templates</p>
          </div>
        </div>
        
        {connection?.status === 'active' && (
          <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
            <CheckCircleIcon className="w-4 h-4" />
            Connected
          </div>
        )}
        {connection?.status === 'error' && (
          <div className="flex items-center gap-2 px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm font-medium">
            <ExclamationTriangleIcon className="w-4 h-4" />
            Error
          </div>
        )}
      </div>

      {/* Connection Details */}
      {connection && siteUrl && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 font-medium">Site:</span>
            <a 
              href={siteUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline flex items-center gap-1"
            >
              {siteName || 'SharePoint Site'}
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
          
          {foldersSetup && (
            <div className="pt-2 border-t border-gray-200">
              <span className="text-gray-600 font-medium block mb-1">Folders:</span>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                  <FolderIcon className="w-3 h-3 text-blue-500" />
                  Templates
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                  <FolderIcon className="w-3 h-3 text-green-500" />
                  Generated Contracts
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                  <FolderIcon className="w-3 h-3 text-purple-500" />
                  Signed Contracts
                </span>
              </div>
            </div>
          )}
          
          {connection.lastSyncedAt && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-gray-600">Last tested:</span>
              <span className="text-gray-900">
                {new Date(connection.lastSyncedAt).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Description when not connected */}
      {!connection && (
        <p className="text-gray-600 mb-4 text-sm">
          Connect to Microsoft SharePoint to store contract templates and generated documents. 
          We'll help you set up the necessary folders in your SharePoint site.
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        {!connection ? (
          <button
            onClick={handleConnect}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Connect to SharePoint
          </button>
        ) : (
          <>
            {connection.status === 'pending_auth' ? (
              <>
                <button
                  onClick={handleOpenSetup}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FolderIcon className="w-5 h-5 mr-2" />
                  Complete Setup - Configure Site
                </button>
                <button
                  onClick={handleDisconnect}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Disconnect
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleOpenSetup}
                  disabled={connection.status !== 'active'}
                  className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FolderIcon className="w-4 h-4 mr-2" />
                  Reconfigure Folders
                </button>
                
                <button
                  onClick={handleDisconnect}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Disconnect
                </button>
              </>
            )}
          </>
        )}
      </div>
      
      <ConfirmDialog />
      
      {/* Setup Modal */}
      {showSetupModal && connection && (
        <SharePointSetupModal
          isOpen={showSetupModal}
          onClose={() => setShowSetupModal(false)}
          connectionId={connection.id}
          onComplete={() => {
            setShowSetupModal(false);
            onUpdate();
          }}
        />
      )}
    </div>
  );
}
